import { content } from "./content.js";
import { scrubAnswerHtml, extractBoldPhrases, applyBoldPhrases } from "./sanitize.js";

let savedBody = null;

export function show(panelEl, { question, token }) {
  const body = panelEl.querySelector(".tp-panel__body");
  savedBody = document.createDocumentFragment();
  while (body.firstChild) savedBody.appendChild(body.firstChild);

  const root = create({ question });
  body.appendChild(root);

  startStreaming(root, { token, question });
}

export function reset(panelEl) {
  restoreSavedBody(panelEl);
}

function restoreSavedBody(panelEl) {
  if (!savedBody) return;
  const body = panelEl.querySelector(".tp-panel__body");
  body.innerHTML = "";
  body.appendChild(savedBody);
  savedBody = null;
}

function create({ question }) {
  const root = document.createElement("div");
  root.className = "tp-conversation";
  root.innerHTML = template();
  wire(root, { question });
  return root;
}

function template() {
  return `
    <div class="tp-conversation__question">
      <span class="tp-conversation__question-label"></span>
      <p class="tp-conversation__question-text"></p>
    </div>
    <p class="tp-conversation__disclaimer"></p>
    <div class="tp-conversation__status" role="status">
      <span class="tp-conversation__status-label"></span><span class="tp-conversation__status-dots" aria-hidden="true"></span>
    </div>
    <div class="tp-conversation__answer" aria-live="polite" aria-busy="true"></div>
    <section class="tp-sources" hidden>
      <h3 class="tp-sources__heading"></h3>
      <p class="tp-sources__shepards"></p>
      <ul class="tp-sources__list"></ul>
    </section>
    <div class="tp-conversation__ctas" hidden>
      <a class="tp-cta tp-cta--primary" target="_blank" rel="noopener noreferrer"></a>
      <a class="tp-cta tp-cta--secondary" target="_blank" rel="noopener noreferrer"></a>
    </div>
  `;
}

function wire(root, { question }) {
  root.querySelector(".tp-conversation__question-label").textContent =
    content.conversationQuestionLabel;
  root.querySelector(".tp-conversation__question-text").textContent = question;

  root.querySelector(".tp-conversation__disclaimer").textContent = content.disclaimer;
  root.querySelector(".tp-conversation__status-label").textContent = content.generatingLabel;
}

const REVEAL_INTERVAL_MS = 24;

async function startStreaming(root, { token, question }) {
  const answerEl = root.querySelector(".tp-conversation__answer");
  const statusEl = root.querySelector(".tp-conversation__status");
  let full = "";          // cumulative answer HTML received from the stream
  let shown = 0;          // characters currently revealed
  let streamDone = false; // upstream has finished sending
  let timer = null;
  const sources = {};     // structured citations captured from the stream

  // Hide the "Generating response…" indicator once the answer starts showing.
  const hideStatus = () => statusEl && statusEl.setAttribute("hidden", "");

  const finish = () => {
    answerEl.innerHTML = full;
    // Reproduce the upstream final's bold headings in place (no body swap / reload):
    // wrap the exact phrases the final bolded where they lead a block.
    applyBoldPhrases(answerEl, sources.boldPhrases);
    answerEl.setAttribute("aria-busy", "false");
    hideStatus();
    renderSources(root, sources);
    renderCtas(root);
  };

  // The peer returns the whole answer at once (it aggregates AIConversation's
  // stream server-side), so rendering the snapshot directly would make the whole
  // answer snap in at once. Instead, hold the latest snapshot in `full` and
  // reveal it incrementally on a timer for a typed-in feel. Reveal is tag-aware
  // (safePrefix) so we never cut inside an HTML element.
  const tick = () => {
    if (shown < full.length) {
      hideStatus();
      const step = Math.max(2, Math.ceil(full.length / 450));
      shown = Math.min(full.length, shown + step);
      answerEl.innerHTML = safePrefix(full, shown);
    }
    if (streamDone && shown >= full.length) {
      finish();
      return;
    }
    timer = setTimeout(tick, REVEAL_INTERVAL_MS);
  };
  timer = setTimeout(tick, REVEAL_INTERVAL_MS);

  try {
    // streamAnswer yields the answer-so-far as it streams from the peer (an
    // ever-growing snapshot), so replace `full` on each yield rather than
    // appending. The reveal advances `shown` monotonically toward it.
    for await (const snapshot of streamAnswer({ token, question, sink: sources })) {
      // Clean every snapshot before it can be revealed: unwrap links (not
      // clickable) and drop script/media, since the answer HTML is untrusted.
      full = scrubAnswerHtml(snapshot);
    }
    streamDone = true;
  } catch (err) {
    streamDone = true;
    if (timer) clearTimeout(timer);
    hideStatus();
    console.warn("[TryProtege] stream failed", err);
    renderError(answerEl);
  }
}

function renderError(answerEl) {
  answerEl.innerHTML = "";
  answerEl.setAttribute("aria-busy", "false");
  const block = document.createElement("div");
  block.className = "tp-conversation__error";
  block.setAttribute("role", "alert");
  block.textContent = content.conversationErrorMessage;
  answerEl.appendChild(block);
}

// Largest prefix of `html` up to ~`n` chars that does not end inside an HTML
// tag, so a partially-revealed answer still parses as valid markup.
function safePrefix(html, n) {
  if (n >= html.length) return html;
  const open = html.lastIndexOf("<", n - 1);
  const close = html.lastIndexOf(">", n - 1);
  if (open > close) {
    const end = html.indexOf(">", n);
    return end === -1 ? html.slice(0, open) : html.slice(0, end + 1);
  }
  return html.slice(0, n);
}

// TryProtege feature-peer host per host page, resolved at RUNTIME — not baked in
// at build time. The same tryprotege.js is built once and promoted across
// environments (cert → prod), so a build-time constant can't distinguish them.
// Requests target the Maximus appliance, which routes the `/f/tryprotege/*`
// prefix to the TryProtege feature peer. The peer proxies conversation creation
// to AIConversation internally with the appropriate service identity attached,
// so this client is decoupled from AIConversation at this layer.
// Any unmapped host (incl. localhost in dev) falls back to "" → a relative path,
// which the webpack dev-server proxy forwards to a locally-running peer.
//   cert → cdc1c   (cert-www.lexisnexis.com)
//   prod → pdc1c   (www.lexisnexis.com) — inferred from the Maximus appliance
//     buckets cdc1c-la-maximus / pdc1c-la-maximus; confirm the prod host and that
//     the prod page's CSP allow-lists it.
const TRY_PROTEGE_BASE_BY_HOST = {
  "cert-www.lexisnexis.com": "https://cdc1c-maximustryprotegeus.route53.lexis.com",
  "www.lexisnexis.com": "https://pdc1c-maximustryprotegeus.route53.lexis.com",
};

const CURRENT_HOST =
  typeof window !== "undefined" && window.location ? window.location.hostname : "";
const TRY_PROTEGE_BASE = TRY_PROTEGE_BASE_BY_HOST[CURRENT_HOST] ?? "";

// Streams the conversation answer from the TryProtege feature peer
// (`POST /f/tryprotege/conversation/new`). The peer proxies to AIConversation
// internally (attaching the service identity) and forwards its SSE stream as
// `text/event-stream`, so this client reads the response body incrementally and
// yields the answer-so-far as it grows. We pass `renderHtml: true`, so the peer
// renders the Markdown answer to HTML server-side and each `delta` is a cumulative
// HTML snapshot (replace, not append). The `finalAnswer` body may differ from the
// last delta (it uses upstream authoritative HTML) and also supplies citations.
// The async-iterable contract is unchanged — startStreaming's reveal timer paces the content.
async function* streamAnswer({ token, question, sink }) {
  // The trial unlock token is a signed anonymous-ask JWT minted by the Panel 2
  // lead form. The deployed bundle calls the Maximus appliance, whose JWT auth
  // scheme validates the `Authorization: Bearer <token>` header and forwards the
  // request to the peer with a WAM identity attached. Without it the request is
  // anonymous and the auth challenge surfaces as the Protégé error state. Only
  // set it when present so the local dev/proxy path (which reaches a peer
  // running under its fallback WAM user) is unchanged.
  const headers = { "Content-Type": "application/json", "Accept": "text/event-stream" };
  if (token) headers.Authorization = `Bearer ${token}`;
  // `renderHtml: true` asks the peer to render the Markdown answer to HTML and stream
  // cumulative HTML snapshots — the widget has no Markdown renderer. (The ask-protege
  // page omits the flag and keeps receiving raw Markdown deltas.)
  // The request body is typed against the NSwag-generated contract so a rename of the
  // backend's request fields (e.g. `prompt` / `renderHtml`) fails `npm run typecheck`.
  /** @type {import("../auto/autotryprotege-abstractions").NewConversationRequest} */
  const requestBody = { prompt: question, renderHtml: true };
  const res = await fetch(`${TRY_PROTEGE_BASE}/f/tryprotege/conversation/new`, {
    method: "POST",
    headers,
    body: JSON.stringify(requestBody),
  });
  if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

  // Each SSE `data:` frame is a ConversationEvent: an early `{ conversationId }`,
  // running `{ delta }` chunks, a final `{ finalAnswer: { content, lnCitations,
  // webCitations } }`, or `{ error }`. Read the body incrementally, buffering
  // partial lines, and yield the accumulated answer text as it grows.
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let content = "";

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    let newlineIndex;
    while ((newlineIndex = buffer.indexOf("\n")) !== -1) {
      const line = buffer.slice(0, newlineIndex).trim();
      buffer = buffer.slice(newlineIndex + 1);

      if (!line.startsWith("data:")) continue; // skip heartbeats / blank lines

      const data = line.slice("data:".length).trim();
      if (!data || data === "[DONE]") continue;

      // Typed against the NSwag-generated contract (auto/): field access
      // below (delta, finalAnswer.content, lnCitations, …) is checked by
      // `npm run typecheck`, so a breaking backend change fails the build.
      /** @type {import("../auto/autotryprotege-abstractions").ConversationEvent} */
      let event;
      try {
        event = JSON.parse(data);
      } catch {
        continue; // ignore malformed frames
      }

      if (event.error) throw new Error(event.error);

      if (event.delta) {
        // Each delta is the full HTML-so-far snapshot (renderHtml) — replace, don't append.
        content = event.delta;
        yield content;
      } else if (event.finalAnswer) {
        if (sink) {
          if (Array.isArray(event.finalAnswer.lnCitations)) sink.lnCitations = event.finalAnswer.lnCitations;
          if (Array.isArray(event.finalAnswer.webCitations)) sink.webCitations = event.finalAnswer.webCitations;
          // The upstream final bolds the headings/labels; capture those exact
          // phrases so finish() can reproduce them on the streamed answer.
          sink.boldPhrases = extractBoldPhrases(event.finalAnswer.content);
        }
        // Keep the streamed (delta) answer as the final body — do NOT swap in the
        // upstream html.text. The deltas already render the whole answer as
        // formatted HTML (headings are real <h1>/<h2> and bold via the answer CSS),
        // so swapping to the upstream final only re-renders the entire block (the
        // visible "reload") to add <strong> on plain-text labels and inline citation
        // markup. The final frame is used only for the Sources citations (above).
        // Fall back to the final body only if no delta ever arrived.
        if (!content) content = event.finalAnswer.content || "";
        yield content;
      }
    }
  }
}

function renderSources(root, sources = {}) {
  // Build display items from the stream's structured citations.
  const ln = (sources.lnCitations || []).map((c) => ({
    type: labelForContentType(c.contentType),
    title: c.title,
    citation: c.jurisdiction || c.court || "",
  }));
  const web = (sources.webCitations || []).map((c) => ({
    type: "Web",
    title: c.title,
    citation: hostnameOf(c.url),
  }));
  const items = [...ln, ...web].filter((s) => s.title);

  const section = root.querySelector(".tp-sources");
  const list = section.querySelector(".tp-sources__list");
  list.innerHTML = "";

  // Nothing to show (or upstream returned no citations) — hide the section.
  if (items.length === 0) {
    section.hidden = true;
    return;
  }

  section.querySelector(".tp-sources__heading").textContent = content.sourcesHeading;
  section.querySelector(".tp-sources__shepards").textContent = content.shepardsLine;

  for (const source of items) {
    const li = document.createElement("li");
    li.className = "tp-source";

    const type = document.createElement("span");
    type.className = "tp-source__type";
    type.textContent = source.type;
    li.appendChild(type);

    // Non-clickable: render the source title as plain text, not a link.
    const title = document.createElement("span");
    title.className = "tp-source__title";
    title.textContent = source.title;
    li.appendChild(title);

    if (source.citation) {
      const citation = document.createElement("p");
      citation.className = "tp-source__citation";
      citation.textContent = source.citation;
      li.appendChild(citation);
    }

    list.appendChild(li);
  }
  section.hidden = false;
}

// Reduces a full URL to its bare domain (e.g. "floridarevenue.com") for a clean
// citation line; falls back to the raw string if it isn't a parseable URL.
function hostnameOf(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url || "";
  }
}

// Maps an lnCitation contentType to a short display label.
function labelForContentType(contentType) {
  switch ((contentType || "").toLowerCase()) {
    case "cases": return "Case";
    case "statutes-legislation": return "Statute";
    case "secondary-materials": return "Secondary";
    default: return "Source";
  }
}

function renderCtas(root) {
  const wrap = root.querySelector(".tp-conversation__ctas");
  const [primary, secondary] = wrap.querySelectorAll(".tp-cta");
  primary.textContent = content.ctaExploreLabel;
  primary.href = content.ctaExploreHref;
  secondary.textContent = content.ctaDeepDiveLabel;
  secondary.href = content.ctaDeepDiveHref;
  wrap.hidden = false;
}

